import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlanSelector } from '@/components/billing/plan-selector';
import { PlanCard } from '@/components/billing/plan-card';
import { useSubscription } from '@/hooks/subscription';
import { useUpgradeSubscription } from '@/hooks/subscription';
import { subscriptionService, type SubscriptionPlan } from '@/services/subscription/subscription.service';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft, Shield } from 'lucide-react';

type PlanTier = 'MINI' | 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ENTERPRISE';

export default function UpgradePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription();
  const upgradeMutation = useUpgradeSubscription();
  const [selectedPlanTier, setSelectedPlanTier] = useState<PlanTier | null>(null);

  const { data: plans, isLoading: isLoadingPlans } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const response = await subscriptionService.getPlans();
      return Array.isArray(response) ? response : response.data || [];
    },
  });

  // Filter plans to only show upgrades (higher price than current)
  const upgradePlans = useMemo(() => {
    if (!plans || !subscription) return [];
    
    const currentPrice = subscription.plan.priceInCents;
    return plans.filter(plan => plan.priceInCents > currentPrice && plan.isActive);
  }, [plans, subscription]);

  // Group plans by tier
  const plansByTier = useMemo<Record<PlanTier, SubscriptionPlan[]>>(() => {
    if (!upgradePlans) return {} as Record<PlanTier, SubscriptionPlan[]>;
    
    return upgradePlans.reduce((acc, plan) => {
      const tier = plan.name as PlanTier;
      if (!acc[tier]) acc[tier] = [];
      acc[tier].push(plan);
      return acc;
    }, {} as Record<PlanTier, SubscriptionPlan[]>);
  }, [upgradePlans]);

  const availableTiers = Object.keys(plansByTier) as PlanTier[];

  // Set default selected tier
  if (!selectedPlanTier && availableTiers.length > 0) {
    setSelectedPlanTier(availableTiers[0]);
  }

  const selectedPlans: SubscriptionPlan[] = selectedPlanTier && selectedPlanTier in plansByTier 
    ? plansByTier[selectedPlanTier] || [] 
    : [];
  const currentPlanPrice = subscription?.plan.priceInCents || 0;

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    // Redirect to checkout page instead of direct upgrade
    // This allows users to see prorating and select months
    navigate(`/billing/checkout?planId=${plan.id}`);
  };

  const isLoading = isLoadingSubscription || isLoadingPlans;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/billing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('billing.backToBilling')}
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.upgrade.noSubscription')}</CardTitle>
            <CardDescription>{t('billing.upgrade.noSubscriptionDescription')}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (upgradePlans.length === 0) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/billing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('billing.backToBilling')}
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.upgrade.noUpgradesAvailable')}</CardTitle>
            <CardDescription>{t('billing.upgrade.noUpgradesAvailableDescription')}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/billing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('billing.backToBilling')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('billing.upgrade.title')}</h1>
          <p className="text-sm text-slate-600 mt-1">{t('billing.upgrade.subtitle')}</p>
        </div>
      </div>

      {/* Current Plan Info */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardHeader>
          <CardTitle className="text-emerald-900">{t('billing.upgrade.currentPlan')}</CardTitle>
          <CardDescription className="text-emerald-700">
            {subscription.plan.displayName} - ${subscription.plan.priceInCents}/{t('billing.month')}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Plan Selector */}
      {availableTiers.length > 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t('billing.upgrade.selectPlanTier')}</h2>
          <PlanSelector
            selectedPlan={selectedPlanTier}
            onPlanChange={setSelectedPlanTier}
            availablePlans={availableTiers}
          />
        </div>
      )}

      {/* Available Plans */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">{t('billing.upgrade.availablePlans')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {selectedPlans.map((plan: SubscriptionPlan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isSelected={false}
              canUpgrade={plan.priceInCents > currentPlanPrice}
              onSelect={() => handleSelectPlan(plan)}
            />
          ))}
        </div>
      </div>

      {/* Security Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <CardTitle className="text-blue-900 text-base">{t('billing.upgrade.securityNote')}</CardTitle>
              <CardDescription className="text-blue-800 mt-1">
                {t('billing.upgrade.securityDescription')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}

